import mammoth from 'mammoth/mammoth.browser';
import { DEFAULT_RESUME } from '../config/constants';

const headingMap = {
  'professional summary': 'professional_summary',
  'executive summary': 'professional_summary',
  'career summary': 'professional_summary',
  'profile summary': 'professional_summary',
  'summary': 'summary',
  'about': 'summary',
  'about me': 'summary',
  'experience': 'experience',
  'work experience': 'experience',
  'employment history': 'experience',
  'professional experience': 'experience',
  'professional experience highlights': 'experience',
  'work history': 'experience',
  'relevant experience': 'experience',
  'volunteer experience': 'experience',
  'volunteer': 'experience',
  'education': 'education',
  'education and training': 'education',
  'academic background': 'education',
  'academic history': 'education',
  'skills': 'skills',
  'technical skills': 'skills',
  'core skills': 'core_skills',
  'core competencies': 'core_skills',
  'key skills': 'core_skills',
  'areas of expertise': 'core_skills',
  'key competencies': 'core_skills',
  'competencies': 'core_skills',
  'certifications': 'certifications',
  'specialized certification': 'certifications',
  'specialized certifications': 'certifications',
  'licenses': 'certifications',
  'certifications and licenses': 'certifications',
  'licenses and certifications': 'certifications',
  'projects': 'projects',
  'training': 'training',
  'professional development': 'training',
  'professional training': 'training',
  'languages': 'languages',
  'languages spoken': 'languages',
  'awards': 'awards',
  'honors': 'awards',
  'awards and honors': 'awards',
  'honors and awards': 'awards'
};

const extractSections = (text) => {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const sections = { header: [] };
  let current = 'header';

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      sections[current].push('');
      return;
    }
    // Strip trailing colons/punctuation before matching headings
    const normalized = trimmed.toLowerCase().replace(/[:;.\-–]+$/, '').trim();
    const key = headingMap[normalized];
    if (key) {
      current = key;
      if (!sections[current]) sections[current] = [];
      return;
    }
    // Fallback: check if line has "Heading: Content" on a single line
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx > 0) {
      const prefix = trimmed.substring(0, colonIdx).trim().toLowerCase().replace(/[:;.\-–]+$/, '').trim();
      const inlineKey = headingMap[prefix];
      if (inlineKey) {
        current = inlineKey;
        if (!sections[current]) sections[current] = [];
        const remainder = trimmed.substring(colonIdx + 1).trim();
        if (remainder) sections[current].push(remainder);
        return;
      }
    }
    if (!sections[current]) sections[current] = [];
    sections[current].push(trimmed);
  });

  return sections;
};

// Month name to number lookup
const MONTH_MAP = {
  jan: '01', january: '01',
  feb: '02', february: '02',
  mar: '03', march: '03',
  apr: '04', april: '04',
  may: '05',
  jun: '06', june: '06',
  jul: '07', july: '07',
  aug: '08', august: '08',
  sep: '09', sept: '09', september: '09',
  oct: '10', october: '10',
  nov: '11', november: '11',
  dec: '12', december: '12'
};

// Parse a date string like "Jan 2020", "January 2020", "2020", "Present"
const parseSingleDate = (str) => {
  const s = (str || '').trim();
  if (!s) return { month: '', year: '' };
  if (/present|current|now|ongoing/i.test(s)) return { month: '', year: '', isCurrent: true };
  const monthYear = s.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYear) {
    const m = MONTH_MAP[monthYear[1].toLowerCase()] || '';
    return { month: m, year: monthYear[2] };
  }
  const yearOnly = s.match(/^(\d{4})$/);
  if (yearOnly) return { month: '', year: yearOnly[1] };
  const slashDate = s.match(/^(\d{1,2})[/.-](\d{4})$/);
  if (slashDate) return { month: String(slashDate[1]).padStart(2, '0'), year: slashDate[2] };
  return { month: '', year: '' };
};

// Extract date range from a line
const extractDateRange = (line) => {
  const s = String(line || '');
  const rangeMatch = s.match(/([A-Za-z]*\s*\d{1,4}[/.-]?\d{0,4})\s*[-–—to]+\s*([A-Za-z]*\s*\d{0,4}[/.-]?\d{0,4}|[Pp]resent|[Cc]urrent|[Nn]ow|[Oo]ngoing)/);
  if (rangeMatch) {
    const start = parseSingleDate(rangeMatch[1]);
    const end = parseSingleDate(rangeMatch[2]);
    return {
      start_month: start.month || '',
      start_year: start.year || '',
      end_month: end.isCurrent ? '' : (end.month || ''),
      end_year: end.isCurrent ? '' : (end.year || ''),
      is_current: !!end.isCurrent
    };
  }
  return null;
};

// Detect location patterns
const extractLocation = (line) => {
  const s = String(line || '');
  if (/\bremote\b/i.test(s)) return 'Remote';
  const locMatch = s.match(/([A-Z][a-zA-Z\s]+),\s*([A-Z]{2})\b/);
  if (locMatch) return `${locMatch[1].trim()}, ${locMatch[2]}`;
  const locMatch2 = s.match(/([A-Z][a-zA-Z\s]+),\s*([A-Z][a-zA-Z]+)\s*$/);
  if (locMatch2) return `${locMatch2[1].trim()}, ${locMatch2[2].trim()}`;
  return '';
};

const parseContact = (text) => {
  const s = String(text || '');
  const emailMatch = s.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  const phoneMatch = s.match(/(\+?\d[\d\s().-]{8,}\d)/);
  const linkedinMatch = s.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[A-Za-z0-9_-]+\/?/i);
  const websiteMatch = s.match(/(?:https?:\/\/)?(?:www\.)?(?!linkedin\.com)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?/i);
  const locationMatch = s.match(/([A-Z][a-zA-Z\s]+),\s*([A-Z]{2})\s*(?:\d{5})?/);
  return {
    contact_email: emailMatch ? emailMatch[0] : '',
    contact_phone: phoneMatch ? phoneMatch[0].trim() : '',
    linkedin_url: linkedinMatch ? (linkedinMatch[0].startsWith('http') ? linkedinMatch[0] : 'https://' + linkedinMatch[0]) : '',
    website_url: websiteMatch ? (websiteMatch[0].startsWith('http') ? websiteMatch[0] : 'https://' + websiteMatch[0]) : '',
    location: locationMatch ? `${locationMatch[1].trim()}, ${locationMatch[2]}` : ''
  };
};

const splitBlocks = (lines = []) => {
  const joined = lines.join('\n');
  return joined
    .split(/\n{2,}/)
    .map((block) => block.split('\n').map((l) => l.trim()).filter(Boolean))
    .filter((block) => block.length > 0);
};

const splitExperienceBlocksByDate = (lines = []) => {
  const nonEmpty = lines.map(l => String(l || '').trim()).filter(Boolean);
  if (nonEmpty.length === 0) return [];
  const blocks = [];
  let current = [];
  for (const line of nonEmpty) {
    const dateInfo = extractDateRange(line);
    const isDateBoundary = dateInfo
      && dateInfo.start_year
      && dateInfo.start_year.length === 4
      && line.length < 80;
    if (isDateBoundary && current.length > 0) {
      blocks.push(current);
      current = [line];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) blocks.push(current);
  return blocks.filter(b => b.length > 0);
};

const isDescriptionLine = (line) => {
  const s = String(line || '').trim();
  if (/^[•\-\*▪▸►◆○●·⬥]\s/.test(s)) return true;
  if (s.length > 120) return true;
  const actionVerbs = /^(Led|Managed|Developed|Created|Oversaw|Coordinated|Planned|Maximized|Increased|Revised|Provided|Trained|Collaborated|Selected|Set|Used|Implemented|Designed|Built|Established|Maintained|Supported|Directed|Supervised|Analyzed|Prepared|Delivered|Executed|Administered|Organized|Initiated|Improved|Facilitated|Conducted|Achieved|Negotiated|Spearheaded|Streamlined|Pioneered|Transformed|Resolved|Ensured|Communicated|Authored|Drove|Optimized|Launched|Assessed|Monitored|Evaluated|Reviewed|Drafted|Reported|Handled|Performed|Processed|Assisted|Mentored|Served|Taught|Guided|Deployed|Configured|Automated|Integrated|Migrated|Scaled|Tested|Validated|Researched|Engineered|Cultivated|Advocated|Championed|Formulated|Restructured)\b/i;
  if (actionVerbs.test(s)) return true;
  return false;
};

const parseExperienceBlocks = (lines = []) => {
  return splitExperienceBlocksByDate(lines).map((block) => {
    const allLines = [...block];
    let title = '';
    let company = '';
    let dateInfo = null;
    let location = '';
    const descLines = [];

    const first = allLines.shift() || '';
    const firstDateCheck = extractDateRange(first);

    if (firstDateCheck) {
      dateInfo = firstDateCheck;
      const metaLines = [];
      while (allLines.length > 0) {
        const next = allLines[0];
        if (isDescriptionLine(next)) break;
        metaLines.push(allLines.shift());
      }
      if (metaLines.length > 0) {
        title = metaLines.pop() || '';
      }
      for (const ml of metaLines) {
        const loc = extractLocation(ml);
        if (loc && !location) { location = loc; continue; }
        if (!company) { company = ml; continue; }
        company = ml + ' | ' + company;
      }
      descLines.push(...allLines);
    } else if (first.includes(' - ') && !isDescriptionLine(first)) {
      const parts = first.split(' - ');
      title = parts[0].trim();
      company = parts.slice(1).join(' - ').trim();
      for (const line of allLines) {
        if (!dateInfo) {
          const dr = extractDateRange(line);
          if (dr) {
            dateInfo = dr;
            const locOnDateLine = extractLocation(line.replace(/[A-Za-z]*\s*\d{1,4}[/.-]?\d{0,4}\s*[-–—to]+\s*[A-Za-z]*\s*\d{0,4}[/.-]?\d{0,4}|present|current/gi, ''));
            if (locOnDateLine) location = locOnDateLine;
            continue;
          }
        }
        if (!location) {
          const loc = extractLocation(line);
          if (loc) { location = loc; continue; }
        }
        descLines.push(line);
      }
    } else {
      title = first;
      for (const line of allLines) {
        if (!dateInfo) {
          const dr = extractDateRange(line);
          if (dr) {
            dateInfo = dr;
            const locOnDateLine = extractLocation(line.replace(/[A-Za-z]*\s*\d{1,4}[/.-]?\d{0,4}\s*[-–—to]+\s*[A-Za-z]*\s*\d{0,4}[/.-]?\d{0,4}|present|current/gi, ''));
            if (locOnDateLine) location = locOnDateLine;
            continue;
          }
        }
        if (!company && !dateInfo && !isDescriptionLine(line)) {
          company = line;
          continue;
        }
        if (!location && !isDescriptionLine(line)) {
          const loc = extractLocation(line);
          if (loc) { location = loc; continue; }
        }
        descLines.push(line);
      }
    }

    return {
      title,
      company,
      start_month: dateInfo ? dateInfo.start_month : '',
      start_year: dateInfo ? dateInfo.start_year : '',
      end_month: dateInfo ? dateInfo.end_month : '',
      end_year: dateInfo ? dateInfo.end_year : '',
      is_current: dateInfo ? dateInfo.is_current : false,
      location,
      description: descLines.join('\n')
    };
  });
};

const parseEducationBlocks = (lines = []) => {
  return splitBlocks(lines).map((block) => {
    const [first, second, ...rest] = block;
    let dateInfo = null;
    const descLines = [];
    for (const line of rest) {
      if (!dateInfo) {
        const dr = extractDateRange(line);
        if (dr) { dateInfo = dr; continue; }
      }
      descLines.push(line);
    }
    return {
      degree: first || '',
      institution: second || '',
      start_year: dateInfo ? dateInfo.start_year : '',
      end_year: dateInfo ? dateInfo.end_year : '',
      description: descLines.join('\n')
    };
  });
};

const parseListSection = (lines = []) => {
  const joined = lines.join(' ');
  return joined
    .split(/[,•|]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseCoreSkills = (lines = []) => {
  const items = parseListSection(lines);
  return items.slice(0, 10).map((name, idx) => ({ id: Date.now() + idx, name }));
};

export const extractTextFromDocx = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result?.value || '';
};

export const parseResumeText = (text) => {
  const cleanedText = String(text || '');
  const sections = extractSections(cleanedText);
  const headerLines = sections.header.filter((line) => line && line.trim());

  const title = headerLines[0] || DEFAULT_RESUME.title;
  const headline = headerLines.length > 1 ? headerLines[1] : '';
  const contact = parseContact(cleanedText);

  const professionalSummary = (sections.professional_summary || []).join('\n').trim();
  const summary = professionalSummary ? '' : (sections.summary || []).join('\n').trim();

  return {
    ...DEFAULT_RESUME,
    title,
    headline,
    professional_summary: professionalSummary,
    summary,
    ...contact,
    experience: parseExperienceBlocks(sections.experience || []),
    education: parseEducationBlocks(sections.education || []),
    skills: parseListSection(sections.skills || []),
    core_skills: parseCoreSkills(sections.core_skills || []),
    certifications: parseListSection(sections.certifications || []),
    projects: parseExperienceBlocks(sections.projects || []),
    training: parseListSection(sections.training || []),
    languages: parseListSection(sections.languages || []),
    awards: parseListSection(sections.awards || [])
  };
};
